import type { LiveVehicle } from "@/types/tracking";

/**
 * Pure logic for the tracking vehicle list panel (TASK-5036).
 *
 * Everything here is a pure function on purpose: the counting, filtering and
 * formatting rules are the part of the panel that can be *wrong* (a predicate
 * that drops a vehicle, a zero printed where a measurement was never taken),
 * and a pure function is the only shape a unit test can pin without rendering.
 */

/** The three states a vehicle can be in on this page. */
export type VehicleState = "moving" | "stopped" | "offline";

/** The filter chips above the list: «Все / Едут / Стоят / Офлайн». */
export type VehicleStatusFilter = "all" | VehicleState;

/**
 * The display state of a single vehicle — READ from the API, not re-derived.
 *
 * The first pass computed this client-side by mirroring `STOPPED_BELOW_KMH =
 * 3.0` from `core/src/apps/tracking/thresholds.py`, because the snapshot only
 * carried `status: online | offline`. That second copy of a threshold this
 * platform owns would have drifted the day it was tuned or made
 * tenant-configurable — and silently, with the list labelling a vehicle «едет»
 * while the header counted it «стоит».
 *
 * The backend now sends `state` outright, decided with the same constant the
 * events feed and the activity strip use. The fallback below exists only for a
 * response predating that field; it is NOT a second definition to maintain, and
 * should be deleted once no deployed backend can omit `state`.
 */
export function getVehicleState(vehicle: LiveVehicle): VehicleState {
  if (vehicle.state) return vehicle.state;
  return vehicle.status === "offline" ? "offline" : "stopped";
}

/** Per-state counts plus the total, for the filter chips and the tab header. */
export interface VehicleStatusCounts {
  all: number;
  moving: number;
  stopped: number;
  offline: number;
}

/** Counts the fleet by derived state. Used for the chips and «Транспорт N». */
export function countByState(vehicles: LiveVehicle[]): VehicleStatusCounts {
  const counts: VehicleStatusCounts = {
    all: vehicles.length,
    moving: 0,
    stopped: 0,
    offline: 0,
  };
  for (const vehicle of vehicles) {
    counts[getVehicleState(vehicle)] += 1;
  }
  return counts;
}

/**
 * Normalizes a string for search: lower-cased, with every character that is not
 * a letter or a digit removed.
 *
 * Plate numbers are the reason. The API returns «1 BB 4245-7»; a dispatcher
 * types «bb42457» or «4245-7» or «1BB». Comparing the raw strings would match
 * none of those, and a naive `trim().toLowerCase()` would still fail on the
 * spaces and the hyphen. Stripping the separators from BOTH sides makes every
 * one of those queries hit.
 */
function normalize(value: string): string {
  return value.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, "");
}

/**
 * Filters by the search box («Номер, машина, водитель»).
 *
 * Matches against the plate, the vehicle name and the driver name. A blank or
 * whitespace-only query matches everything — an empty box is not a filter.
 */
export function filterBySearch(
  vehicles: LiveVehicle[],
  query: string,
): LiveVehicle[] {
  const needle = normalize(query);
  if (needle === "") return vehicles;
  return vehicles.filter((vehicle) => {
    const haystacks = [
      vehicle.vehicle_name,
      vehicle.plate_number ?? "",
      vehicle.driver_name ?? "",
    ];
    return haystacks.some((field) => normalize(field).includes(needle));
  });
}

/** Filters by the active status chip. `"all"` is a pass-through. */
export function filterByState(
  vehicles: LiveVehicle[],
  filter: VehicleStatusFilter,
): LiveVehicle[] {
  if (filter === "all") return vehicles;
  return vehicles.filter((vehicle) => getVehicleState(vehicle) === filter);
}

/**
 * Orders the list the way the mockup does: moving first, then stopped, then
 * offline, and alphabetically by name inside each group.
 *
 * Returns a NEW array — the input is the jotai atom's array and must not be
 * sorted in place.
 */
export function sortVehicles(vehicles: LiveVehicle[]): LiveVehicle[] {
  const rank: Record<VehicleState, number> = {
    moving: 0,
    stopped: 1,
    offline: 2,
  };
  return [...vehicles].sort((a, b) => {
    const byState = rank[getVehicleState(a)] - rank[getVehicleState(b)];
    if (byState !== 0) return byState;
    return a.vehicle_name.localeCompare(b.vehicle_name);
  });
}

/**
 * The whole panel pipeline in one place: state filter, then search, then sort.
 *
 * Composed here rather than in the component so the ORDER of the three steps is
 * itself testable.
 */
export function selectVisibleVehicles(
  vehicles: LiveVehicle[],
  filter: VehicleStatusFilter,
  query: string,
): LiveVehicle[] {
  return sortVehicles(filterBySearch(filterByState(vehicles, filter), query));
}

/**
 * The em-dash rendered wherever a value was NOT MEASURED.
 *
 * `distance_km_today: null` means the vehicle produced no usable track today.
 * Printing «0 км» there would claim a measurement that was never taken — the
 * dispatcher would read "this van stood still all day" from a row that
 * actually says "we do not know". The backend is deliberate about sending null
 * rather than 0 (`TrackingService.get_live_snapshot`); the UI must not undo it.
 */
export const EM_DASH = "—";

/**
 * Formats `distance_km_today` / `distance_km_total`.
 * Null → em-dash. Never «0 км» for a null.
 */
export function formatDistanceKm(km: number | null | undefined): string {
  if (km == null) return EM_DASH;
  return `${km.toFixed(1).replace(".", ",")}`;
}

/**
 * Formats `moving_time_min_today` as «3 ч 12 м» / «45 м».
 * Null → em-dash, by the same null-is-not-zero rule.
 */
export function formatDurationMin(
  minutes: number | null | undefined,
  units: { hour: string; minute: string },
): string {
  if (minutes == null) return EM_DASH;
  const whole = Math.max(0, Math.round(minutes));
  const hours = Math.floor(whole / 60);
  const mins = whole % 60;
  if (hours === 0) return `${mins} ${units.minute}`;
  return `${hours} ${units.hour} ${mins} ${units.minute}`;
}

/** The unit a signal age was rounded to, so the caller can pick the label. */
export type SignalAgeUnit = "sec" | "min" | "hour" | "day";

/** A formatted signal age: a number plus the unit it is expressed in. */
export interface SignalAge {
  value: number;
  unit: SignalAgeUnit;
}

/**
 * Turns `offline_for_sec` into the «8 с» / «1 мин» / «22 мин» / «62 дня» the
 * mockup shows.
 *
 * Derived on render from ONE server number, deliberately — not ticked by a
 * timer. A client-side clock beside a server-computed age is a second clock,
 * and the two disagree the moment the page sits open; the 30-second poll is
 * what moves this value.
 *
 * Boundaries (each pinned by a test): < 60 s → seconds; < 60 min → minutes;
 * < 24 h → hours; otherwise days. Each step floors, so 119 seconds is «1 мин»
 * and not «2 мин» — rounding up would claim more staleness than observed.
 *
 * Null (no fix ever recorded) has no age to express and returns null; the
 * caller renders an em-dash.
 */
export function formatSignalAge(
  seconds: number | null | undefined,
): SignalAge | null {
  if (seconds == null) return null;
  const safe = Math.max(0, Math.floor(seconds));
  if (safe < 60) return { value: safe, unit: "sec" };
  const minutes = Math.floor(safe / 60);
  if (minutes < 60) return { value: minutes, unit: "min" };
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return { value: hours, unit: "hour" };
  return { value: Math.floor(hours / 24), unit: "day" };
}
